// EnvSwitcher.tsx — CORE-413: dev-only floating control to flip the app between
// the dev and prod backends — and, on native, the stand (CORE-1507) — at
// runtime (no rebuild). Renders nothing unless the build allows it
// (isEnvSwitchEnabled: dev/preview profiles or a dev client), so it can be
// mounted unconditionally at the app root.
//
// Switching env changes the base host for all REST + SignalR hubs (see
// config/envSwitch.ts + services/apiEndpoint.ts). The stored auth token belongs
// to the previous env, so applying clears it and reloads — you re-log into the
// selected env.
//
// The pill also carries the build id (config/buildInfo.ts) so a bug report can
// name the exact build it was seen on, not just the env.
import React, { useState } from 'react';
import { reloadAppAsync } from 'expo';
import * as Clipboard from 'expo-clipboard';
import { Text, Pressable, Modal, Platform, StyleSheet } from 'react-native';
import {
  isEnvSwitchEnabled,
  isBuildChipVisible,
  runtimeWebEnv,
  runtimeWebSurface,
  activeEnv,
  buildEnv,
  setEnvOverride,
  getEnvOverride,
  type EnvChoice,
  type EnvName,
} from '../../config/envSwitch';
import { buildLabel, buildReport } from '../../config/buildInfo';
import { storage } from '../../utils/storage';
import ErrorReportCheck from './ErrorReportCheck';

const ENV_TITLES: Record<EnvChoice, string> = { dev: 'Dev', staging: 'Staging', prod: 'Prod' };
const ENV_DOMAINS: Record<EnvChoice, string> = {
  dev: 'agdev.tmless.org',
  staging: 'ag1.tmless.org',
  prod: 'agapella.io',
};

// CORE-1507 — стенд предлагаем только на нативке. В вебе окружение задаёт адрес
// страницы, и там список остаётся прежним.
const ENV_OPTIONS: EnvChoice[] =
  Platform.OS === 'web' ? (['dev', 'prod'] as EnvName[]) : ['dev', 'staging', 'prod'];

/** Reload so the new env takes effect. Returns false when the reload could not
 *  be started — the override is already persisted, so the user just restarts
 *  the app. */
async function reloadApp(): Promise<boolean> {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') window.location.reload();
    return true;
  }
  // Native counterpart of the page reload above. Not DevSettings.reload: outside
  // __DEV__ (preview builds) it is an empty stub, so the modal closed with the
  // old session still on screen (CORE-413 QA FAIL). reloadAppAsync reloads the
  // JS bundle in release and dev-client builds alike.
  try {
    await reloadAppAsync('Env switch');
    return true;
  } catch {
    return false;
  }
}

export default function EnvSwitcher(): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [restartHint, setRestartHint] = useState(false);
  const [copied, setCopied] = useState(false);

  // Плашку убирали с нативки 28.08.2026 — она висела поверх каждого экрана
  // dev-сборки. Возвращаем: на телефоне это ЕДИНСТВЕННЫЙ способ узнать, с
  // каким бэкендом говорит билд, и переключить его. Без неё QA описывает баг,
  // не зная стенда, а аккаунты у дева и прода разные — отсюда репорты вида
  // «не могу зайти пользователем, которым только что заходил в андроиде».
  //
  // Гейт остаётся один — isBuildChipVisible(): на нативке это
  // EXPO_PUBLIC_ENV_SWITCHER=true (профили development / development-simulator /
  // preview) либо dev-client. В сторовой сборке переменной нет, поэтому там
  // плашки по-прежнему не будет.
  if (!isBuildChipVisible()) return null;

  // CORE-631 — on the web stand the pill is informational only: switching is
  // gated separately (see isBuildChipVisible).
  const canSwitch = isEnvSwitchEnabled();
  // On web the address bar decides which backend we actually talk to
  // (runtimeWebEnv), so trust it over the baked EXPO_PUBLIC_API_URL — that
  // variable has gone missing on Vercel twice, and a pill that says «prod» on
  // the dev stand sends QA chasing the wrong environment.
  const current = runtimeWebEnv() ?? activeEnv();
  // CORE-1103 — what the pill SAYS is the surface, not the backend env: the
  // stand is neither dev nor prod, and QA reads this line to name where a bug
  // was seen. `current` above stays the backend env, because that is what the
  // switch rows below act on.
  const surface: string = runtimeWebSurface() ?? current;
  const overridden = canSwitch && getEnvOverride() !== null;
  const build = buildLabel();

  const copyReport = async () => {
    try {
      await Clipboard.setStringAsync(buildReport(surface));
      setCopied(true);
    } catch {
      /* clipboard unavailable — the line is still readable on screen */
    }
  };

  const choose = async (env: EnvChoice | null) => {
    setBusy(true);
    await setEnvOverride(env);
    // Token is env-specific — drop it so we re-auth against the selected env.
    await storage.removeToken().catch(() => {});
    const reloaded = await reloadApp();
    setBusy(false);
    if (!reloaded) {
      // The reload could not be started — persisted, so ask the user to
      // restart. Keep the modal open with the hint.
      setRestartHint(true);
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <Pressable
        style={[
          styles.pill,
          current === 'prod'
            ? styles.pillProd
            : current === 'staging'
              ? styles.pillStaging
              : styles.pillDev,
        ]}
        onPress={() => setOpen(true)}
        testID="envSwitcher"
        accessibilityLabel="Переключатель окружения (dev)"
      >
        <Text style={styles.pillText}>
          env: {surface}
          {overridden ? ' •' : ''}
        </Text>
        <Text style={styles.pillBuild} testID="envSwitcherBuild">
          {build}
        </Text>
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)}>
          <Pressable style={styles.card} onPress={() => {}} accessible={false}>
            <Text style={styles.title}>Окружение</Text>
            <Text style={styles.sub}>
              {canSwitch ? (
                <>
                  Билд собран под: <Text style={styles.bold}>{buildEnv()}</Text>. Активно:{' '}
                  <Text style={styles.bold}>{surface}</Text>
                  {overridden ? ' (переопределено)' : ''}
                </>
              ) : (
                <>
                  Окружение: <Text style={styles.bold}>{surface}</Text>
                </>
              )}
            </Text>

            <Pressable
              style={styles.buildBox}
              onPress={copyReport}
              testID="envSwitcherCopyBuild"
              accessibilityRole="button"
              accessibilityLabel="Скопировать данные сборки"
            >
              <Text style={styles.buildLine}>{buildReport(surface)}</Text>
              <Text style={styles.buildHint}>
                {copied ? 'Скопировано' : 'Нажмите, чтобы скопировать для отчёта'}
              </Text>
            </Pressable>

            {canSwitch && ENV_OPTIONS.map((env) => (
              <Pressable
                key={env}
                disabled={busy}
                style={[styles.row, current === env && styles.rowActive]}
                onPress={() => choose(env)}
                testID={`envOption-${env}`}
                accessibilityRole="button"
                accessibilityLabel={ENV_TITLES[env]}
              >
                <Text style={[styles.rowText, current === env && styles.rowTextActive]}>
                  {ENV_TITLES[env]} ({ENV_DOMAINS[env]})
                  {current === env ? '  ✓' : ''}
                </Text>
              </Pressable>
            ))}

            {canSwitch && (
              <Pressable
                disabled={busy}
                style={styles.reset}
                onPress={() => choose(null)}
                testID="envOption-reset"
                accessibilityRole="button"
                accessibilityLabel="Сбросить окружение"
              >
                <Text style={styles.resetText}>Сбросить к билду ({buildEnv()})</Text>
              </Pressable>
            )}

            {/* CORE-1998 — native test builds only; the web stand has no switcher. */}
            {canSwitch && Platform.OS !== 'web' && <ErrorReportCheck />}

            {!canSwitch ? (
              <Text style={styles.note}>
                Окружение определяется адресом стенда и здесь не переключается. Переключатель есть в
                сборках EAS dev / preview.
              </Text>
            ) : restartHint ? (
              <Text style={[styles.note, styles.restart]}>
                Окружение сохранено. Перезапустите приложение вручную, чтобы применить.
              </Text>
            ) : (
              <Text style={styles.note}>
                Применение перезапускает приложение и очищает вход — потребуется войти заново в
                выбранном окружении.
              </Text>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  pill: {
    position: 'absolute',
    right: 10,
    bottom: 90,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
    opacity: 0.85,
    zIndex: 9999,
  },
  pillDev: { backgroundColor: '#2563eb' },
  pillProd: { backgroundColor: '#dc2626' },
  pillStaging: { backgroundColor: '#7c3aed' },
  pillText: { color: '#fff', fontSize: 11, fontWeight: '700' },
  pillBuild: { color: '#fff', fontSize: 9, fontWeight: '600', opacity: 0.85 },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 340,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
  },
  title: { fontSize: 18, fontWeight: '700', color: '#181411', marginBottom: 6 },
  sub: { fontSize: 13, color: '#6b6b6b', marginBottom: 10 },
  buildBox: {
    backgroundColor: '#f6f6f6',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 14,
  },
  buildLine: {
    fontSize: 12,
    color: '#181411',
    fontWeight: '700',
    ...Platform.select({ web: { fontFamily: 'monospace' }, default: {} }),
  },
  buildHint: { fontSize: 10, color: '#9a9a9a', marginTop: 3 },
  bold: { fontWeight: '700', color: '#181411' },
  row: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e5e5e5',
    marginBottom: 8,
  },
  rowActive: { borderColor: '#E85D04', backgroundColor: '#fff5ef' },
  rowText: { fontSize: 15, color: '#181411' },
  rowTextActive: { fontWeight: '700', color: '#E85D04' },
  reset: { paddingVertical: 10, alignItems: 'center', marginTop: 2 },
  resetText: { fontSize: 13, color: '#6b6b6b' },
  note: { fontSize: 11, color: '#9a9a9a', marginTop: 10, lineHeight: 15 },
  restart: { color: '#E85D04', fontWeight: '700' },
});
